import { useCallback, useEffect, useState } from "react";
import { Search, UserX, UserCheck, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { fmtNumber } from "@/lib/format";

export default function UsersTab() {
  const auth = useAdminAuth();
  const [data, setData] = useState({ total: 0, items: [] });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [skip, setSkip] = useState(0);
  const limit = 20;

  const load = useCallback(() => {
    const params = new URLSearchParams({ limit: String(limit), skip: String(skip) });
    if (q) params.set("q", q);
    if (role) params.set("role", role);
    auth.apiGet(`/admin/users?${params}`).then((r) => setData(r.data)).catch(() => toast.error("No se pudo cargar usuarios"));
  }, [auth, q, role, skip]);

  useEffect(load, [load]);

  const ban = async (u) => {
    try {
      await auth.apiPost(`/admin/users/${u.id}/ban`, {});
      toast.success("Usuario baneado");
      load();
    } catch { toast.error("Error"); }
  };
  const unban = async (u) => {
    try {
      await auth.apiPost(`/admin/users/${u.id}/unban`, {});
      toast.success("Usuario reactivado");
      load();
    } catch { toast.error("Error"); }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));
  const currentPage = Math.floor(skip / limit) + 1;

  return (
    <div data-testid="tab-users" className="space-y-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Gestión de usuarios · {fmtNumber(data.total)} totales</p>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-mid" />
          <input
            data-testid="users-search"
            placeholder="Buscar por nombre, handle o id..."
            value={q}
            onChange={(e) => { setSkip(0); setQ(e.target.value); }}
            className="w-full bg-cream-card border hairline rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-coral"
          />
        </div>
        <select
          data-testid="users-role-filter"
          value={role}
          onChange={(e) => { setSkip(0); setRole(e.target.value); }}
          className="bg-cream-card border hairline rounded-full px-3 py-2 text-sm outline-none"
        >
          <option value="">Todos los roles</option>
          <option value="creator">Creator</option>
          <option value="user">User</option>
          <option value="business">Business</option>
          <option value="investor">Investor</option>
          <option value="traveler">Traveler</option>
        </select>
      </div>

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5">
        {data.items.length === 0 ? (
          <p className="text-sm text-ink-mid text-center py-8" data-testid="users-empty">Sin resultados.</p>
        ) : data.items.map((u) => (
          <div key={u.id} data-testid={`user-${u.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
            <div className="col-span-12 md:col-span-4 flex items-center gap-3">
              <img src={u.avatar} alt="" className="w-8 h-8 rounded-full bg-cream" />
              <div>
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {u.name}
                  {u.is_pro && <Crown className="w-3 h-3 text-coral" />}
                </p>
                <p className="text-[11px] text-ink-mid font-mono">{u.handle}</p>
              </div>
            </div>
            <div className="col-span-4 md:col-span-2 text-xs">
              <span className="px-2 py-0.5 rounded-full bg-coral/10 text-coral font-mono uppercase tracking-wider text-[10px]">{u.role}</span>
            </div>
            <div className="col-span-4 md:col-span-2 text-xs text-ink-mid">{u.country}</div>
            <div className="col-span-4 md:col-span-2 text-xs font-mono">{fmtNumber(u.sparks)} sp · {u.posts} posts</div>
            <div className="col-span-12 md:col-span-2 flex justify-end">
              {u.is_banned ? (
                <button data-testid={`user-unban-${u.id}`} onClick={() => unban(u)} className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 inline-flex items-center gap-1">
                  <UserCheck className="w-3 h-3" /> Reactivar
                </button>
              ) : (
                <button data-testid={`user-ban-${u.id}`} onClick={() => ban(u)} className="text-[10px] px-2 py-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 inline-flex items-center gap-1">
                  <UserX className="w-3 h-3" /> Banear
                </button>
              )}
            </div>
          </div>
        ))}
      </section>

      <div className="flex items-center justify-between text-xs font-mono text-ink-mid">
        <span data-testid="users-pagination-info">Página {currentPage} de {totalPages}</span>
        <div className="flex gap-2">
          <button data-testid="users-prev" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))} className="px-3 py-1 rounded-full border hairline disabled:opacity-30">‹ Anterior</button>
          <button data-testid="users-next" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)} className="px-3 py-1 rounded-full border hairline disabled:opacity-30">Siguiente ›</button>
        </div>
      </div>
    </div>
  );
}
